import { inject, Injectable } from '@angular/core';
import { ChangePasswordViewModel, CredentialsViewModel, RegisterAgencyUserViewModel, RegisterWholesalerViewModel, TwoFactorCredentialsViewModel } from '@program-suite/data-common';
import { CredentialsWith2FaViewModel, VerifyLoginResponse } from '@program-suite/data-common/program';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { propagateError } from '@program-suite/util-helpers';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);

  /**
   * Authenticate username and password
   * @note does not return anything except 200 status code. Needed before 2FA
   * @param credentials
   * @returns 200 - OK
   * @throws Error if authentication fails
   */
  async validateCredentials(credentials: CredentialsViewModel): Promise<void> {
    try {
      await firstValueFrom(this.http.post(`Authentication/login`, credentials));
    } catch (error) {
      propagateError(error);
      throw new Error('Invalid username or password');
    }
  }

  /**
   * Authenticate username and password against Email2FA
   * @param credentials
   * @returns JWT token
   * @throws Error if 2FA login fails
   */
  async login2fa(credentials: TwoFactorCredentialsViewModel): Promise<string> {
    try {
      const token = await firstValueFrom(
        this.http.post('Authentication/login-2fa', credentials, {
          responseType: 'text',
        }),
      );
      return token as string;
    } catch (error) {
      propagateError(error);
      throw new Error('Invalid 2FA code');
    }
  }

  /**
   * Authenticate username and password
   * @note Currently only used in program portal
   * @param credentials
   * @returns requiresApp2fa: boolean & qrCode: string(if requiresApp2fa is true)
   * @throws Error if authentication fails
   */
  async verifyLogin(credentials: CredentialsViewModel): Promise<VerifyLoginResponse> {
    try {
      const res = await firstValueFrom(
        this.http.post('Authentication/verify-login', credentials, {
          responseType: 'json',
        }),
      );
      return res as VerifyLoginResponse;
    } catch (error) {
      propagateError(error);
      throw new Error('Invalid username or password');
    }
  }

  /**
   * Authenticate username and password against App2FA
   * @note Currently only used in program portal
   * @param credentials
   * @returns JWT token
   * @throws Error if 2FA login fails
   */
  async loginApp2fa(credentials: CredentialsWith2FaViewModel): Promise<string> {
    try {
      const token = await firstValueFrom(
        this.http.post('Authentication/login-2fa', credentials, {
          responseType: 'text',
        }),
      );
      return token as string;
    } catch (error) {
      propagateError(error);
      throw new Error('Invalid 2FA code');
    }
  }

  async registerWholesaler(model: RegisterWholesalerViewModel): Promise<void> {
    try {
      await firstValueFrom(this.http.post('account/register-wholesaler', model));
    } catch (error) {
      propagateError(error);
      throw new Error('Failed to register wholesaler. Please try again.');
    }
  }

  async registerAgencyUser(model: RegisterAgencyUserViewModel): Promise<void> {
    try {
      await firstValueFrom(this.http.post('account/register-agency-user', model));
    } catch (error) {
      propagateError(error);
      throw new Error('Failed to register agent. Please try again.');
    }
  }

  async changePassword(model: ChangePasswordViewModel): Promise<void> {
    try {
      await firstValueFrom(this.http.post('account/change-password', model));
    } catch (error) {
      propagateError(error);
      throw new Error('Failed to change password. Please try again.');
    }
  }

  async resendCode(credentials: CredentialsViewModel): Promise<void> {
    try {
      await firstValueFrom(this.http.post('Authentication/resend-code', credentials));
    } catch (error) {
      propagateError(error);
      throw new Error('Failed to resend code. Please try again.');
    }
  }
}
